import React, { useState, useEffect, useMemo } from 'react';
import { Phone, Mail, MessageSquare, Linkedin, Clock, FileText, Save, CheckCircle, Smartphone } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useTimezone } from '../../hooks/useTimezone';
import { formatInTz } from '../../utils/dateFormatters';
import { fromZonedTime } from 'date-fns-tz';
import InlineEmailThread from './InlineEmailThread';
import InlineWhatsAppThread from './InlineWhatsAppThread';
import InlineLinkedInThread from './InlineLinkedInThread';
import InlineCallTimeline from './InlineCallTimeline';

type ChannelTab = 'activity' | 'email' | 'whatsapp' | 'linkedin' | 'calls' | 'sms' | 'notes';

interface ActivityItem {
  id: string;
  channel: 'email' | 'whatsapp' | 'linkedin' | 'call' | 'milestone';
  direction: 'sent' | 'received' | 'milestone';
  content: string;
  subject?: string;
  timestamp: string;
  outcome?: string;
  duration?: number;
}

interface DealActivityTimelineProps {
  leadId: string;
  leadName?: string;
  companyName?: string;
  agentName?: string;
  dealNotes: string;
  onNotesChange: (val: string) => void;
  onSaveNotes: () => void;
  isSavingNotes: boolean;
}

const DealActivityTimeline: React.FC<DealActivityTimelineProps> = ({
  leadId,
  leadName,
  companyName,
  agentName,
  dealNotes,
  onNotesChange,
  onSaveNotes,
  isSavingNotes,
}) => {
  const tz = useTimezone();
  const [activeTab, setActiveTab] = useState<ChannelTab>('activity');
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [milestones, setMilestones] = useState<ActivityItem[]>([]);


  useEffect(() => {
    if (!leadId) return;
    fetchActivityData();
  }, [leadId]);

  const fetchActivityData = async () => {
    setIsLoading(true);
    try {
      const [waRes, emailRes, linkedinRes, callRes, trackingRes, meetingRes] = await Promise.all([
        supabase.from('whatsapp_logs').select('wa_message_id_internal, from_role, message_text, message_type, created_at').eq('lead_id', leadId).order('created_at', { ascending: false }).limit(50),
        supabase.from('email_logs').select('email_id_internal, from_role, email_text, email_subject_line_text, created_at').eq('lead_id', leadId).order('created_at', { ascending: false }).limit(50),
        supabase.from('linkedin_logs').select('lkdin_message_id_internal, from_role, message_text, message_type, created_at').eq('lead_id', leadId).order('created_at', { ascending: false }).limit(50),
        supabase.from('call_logs').select('call_id_internal, call_outcome, call_attempt, call_summary, call_duration_seconds, created_at').eq('lead_id', leadId).order('created_at', { ascending: false }).limit(50),
        supabase.from('leads_tracking').select('created_at, outreach_start_timestamp, meeting_booking_timestamp').eq('lead_id', leadId).maybeSingle(),
        supabase.from('meetings_booked').select('meeting_date, meeting_time, meeting_booked_at, meeting_status, timezone').eq('lead_id', leadId).maybeSingle(),
      ]);

      const items: ActivityItem[] = [];

      (waRes.data || []).forEach(msg => {
        items.push({
          id: msg.wa_message_id_internal,
          channel: 'whatsapp',
          direction: msg.from_role === 'user' ? 'sent' : 'received',
          content: msg.message_text || (msg.message_type === 'audio' ? '[Voice message]' : '[Media]'),
          timestamp: msg.created_at,
        });
      });

      (emailRes.data || []).forEach(msg => {
        items.push({
          id: msg.email_id_internal,
          channel: 'email',
          direction: msg.from_role === 'user' ? 'sent' : 'received',
          content: msg.email_text || '',
          subject: msg.email_subject_line_text || undefined,
          timestamp: msg.created_at,
        });
      });

      (linkedinRes.data || []).forEach(msg => {
        items.push({
          id: msg.lkdin_message_id_internal,
          channel: 'linkedin',
          direction: msg.from_role === 'user' ? 'sent' : 'received',
          content: msg.message_text || (msg.message_type === 'audio' ? '[Voice message]' : '[Media]'),
          timestamp: msg.created_at,
        });
      });

      (callRes.data || []).forEach(call => {
        items.push({
          id: call.call_id_internal,
          channel: 'call',
          direction: 'sent',
          content: call.call_summary || `Call attempt #${call.call_attempt || '?'}`,
          outcome: call.call_outcome || undefined,
          duration: call.call_duration_seconds || undefined,
          timestamp: call.created_at,
        });
      });

      setActivities(items);

      const ms: ActivityItem[] = [];
      const tracking = trackingRes.data;
      if (tracking?.created_at) {
        ms.push({ id: 'ms-generated', channel: 'milestone', direction: 'milestone', content: 'Lead generated', timestamp: tracking.created_at });
      }
      if (tracking?.outreach_start_timestamp) {
        ms.push({ id: 'ms-outreach', channel: 'milestone', direction: 'milestone', content: 'Outreach started', timestamp: tracking.outreach_start_timestamp });
      }
      if (meetingRes.data?.meeting_booked_at) {
        ms.push({ id: 'ms-meeting-booked', channel: 'milestone', direction: 'milestone', content: 'Meeting booked', timestamp: meetingRes.data.meeting_booked_at });
        const status = meetingRes.data.meeting_status;
        const meetingDate = meetingRes.data.meeting_date;
        const meetingTime = meetingRes.data.meeting_time;
        const meetingTz = meetingRes.data.timezone || tz;
        if (status === 'Attended' && meetingDate && new Date(meetingDate) < new Date()) {
          const localStr = meetingTime ? `${meetingDate}T${meetingTime}` : `${meetingDate}T23:59:59`;
          const attendedTs = fromZonedTime(localStr, meetingTz).toISOString();
          ms.push({ id: 'ms-meeting-attended', channel: 'milestone', direction: 'milestone', content: 'Meeting attended', timestamp: attendedTs });
        }
      } else if (tracking?.meeting_booking_timestamp) {
        ms.push({ id: 'ms-meeting-booked', channel: 'milestone', direction: 'milestone', content: 'Meeting booked', timestamp: tracking.meeting_booking_timestamp });
      }
      setMilestones(ms);
    } catch (err) {
      console.error('[DealActivityTimeline] Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const allItemsSorted = useMemo(() => {
    const allItems = [...activities, ...milestones];
    allItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return allItems;
  }, [activities, milestones]);

  const channelCounts = useMemo(() => ({
    email: activities.filter(a => a.channel === 'email').length,
    whatsapp: activities.filter(a => a.channel === 'whatsapp').length,
    linkedin: activities.filter(a => a.channel === 'linkedin').length,
    calls: activities.filter(a => a.channel === 'call').length,
  }), [activities]);

  const tabs: { key: ChannelTab; label: string; icon: React.ReactNode; count?: number }[] = [
    { key: 'activity', label: 'Activity', icon: <Clock size={14} />, count: activities.length },
    { key: 'email', label: 'Email', icon: <Mail size={14} />, count: channelCounts.email },
    { key: 'whatsapp', label: 'WhatsApp', icon: <MessageSquare size={14} />, count: channelCounts.whatsapp },
    { key: 'linkedin', label: 'LinkedIn', icon: <Linkedin size={14} />, count: channelCounts.linkedin },
    { key: 'calls', label: 'Calls', icon: <Phone size={14} />, count: channelCounts.calls },
    { key: 'sms', label: 'iMessage | SMS', icon: <Smartphone size={14} /> },
    { key: 'notes', label: 'Notes', icon: <FileText size={14} /> },
  ];

  const getChannelIcon = (channel: string, direction: string) => {
    const isReceived = direction === 'received';
    switch (channel) {
      case 'email': return <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isReceived ? 'bg-blue-100' : 'bg-blue-50'}`}><Mail size={15} className="text-blue-600" /></div>;
      case 'whatsapp': return <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isReceived ? 'bg-green-100' : 'bg-green-50'}`}><MessageSquare size={15} className="text-green-600" /></div>;
      case 'linkedin': return <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isReceived ? 'bg-sky-100' : 'bg-sky-50'}`}><Linkedin size={15} className="text-sky-700" /></div>;
      case 'call': return <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#1875FF]/10"><Phone size={15} className="text-[#1875FF]" /></div>;
      case 'milestone': return <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#05E0D1]/10"><CheckCircle size={15} className="text-[#05E0D1]" /></div>;
      default: return <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center"><Clock size={15} className="text-gray-500" /></div>;
    }
  };

  const formatTime = (ts: string) => {
    try {
      return formatInTz(new Date(ts), 'MMM d, yyyy h:mma', tz);
    } catch { return ts; }
  };

  const truncateContent = (text: string, max = 120) => {
    if (!text) return '';
    const clean = text.replace(/\n+/g, ' ').trim();
    return clean.length > max ? clean.slice(0, max) + '...' : clean;
  };

  const renderTabContent = () => {
    if (activeTab === 'notes') {
      return (
        <div className="space-y-3">
          <textarea
            value={dealNotes}
            onChange={e => onNotesChange(e.target.value)}
            placeholder="Add notes about this deal..."
            className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1875FF]/20 focus:border-[#1875FF] resize-none text-sm"
            rows={6}
          />
          <button
            onClick={onSaveNotes}
            disabled={isSavingNotes}
            className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white py-2.5 px-4 rounded-xl hover:shadow-lg transition-all text-sm font-bold disabled:opacity-50"
          >
            {isSavingNotes ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
            {isSavingNotes ? 'Saving...' : 'Save Notes'}
          </button>
        </div>
      );
    }

    if (activeTab === 'email') {
      return <InlineEmailThread leadId={leadId} leadName={leadName} agentName={agentName} />;
    }

    if (activeTab === 'whatsapp') {
      return <InlineWhatsAppThread leadId={leadId} leadName={leadName} companyName={companyName} />;
    }

    if (activeTab === 'linkedin') {
      return <InlineLinkedInThread leadId={leadId} leadName={leadName} companyName={companyName} agentName={agentName} />;
    }

    if (activeTab === 'calls') {
      return <InlineCallTimeline leadId={leadId} leadName={leadName} />;
    }

    if (activeTab === 'sms') {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center mb-4">
            <Smartphone size={24} className="text-[#1875FF]" />
          </div>
          <h4 className="text-sm font-semibold text-gray-800 mb-1">iMessage & SMS</h4>
          <p className="text-xs text-gray-500">Coming Soon</p>
        </div>
      );
    }

    // Activity (all) tab - condensed overview
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#1875FF] border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (allItemsSorted.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Clock size={20} className="text-gray-400" />
          </div>
          <p className="text-sm text-gray-500 font-medium">No activity yet</p>
          <p className="text-xs text-gray-400 mt-1">Messages and interactions will appear here</p>
        </div>
      );
    }

    return (
      <div className="max-h-[520px] overflow-y-auto custom-scrollbar">
        <div className="space-y-1">
          {allItemsSorted.map(item => (
            <div key={item.id} className="flex gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group">
              {getChannelIcon(item.channel, item.direction)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-900 capitalize">
                      {item.channel === 'milestone' ? item.content :
                       item.direction === 'sent' ? `${item.channel} Agent` : 'Prospect'}
                    </span>
                    {item.direction !== 'milestone' && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        item.direction === 'sent' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'
                      }`}>
                        {item.direction === 'sent' ? 'SENT' : 'RECEIVED'}
                      </span>
                    )}
                    {item.outcome && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">
                        {item.outcome}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">{formatTime(item.timestamp)}</span>
                </div>
                {item.channel !== 'milestone' && (
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    {item.channel === 'email' && item.subject
                      ? <span className="font-medium text-gray-700">{item.subject}</span>
                      : truncateContent(item.content)}
                  </p>
                )}
                {item.duration != null && item.duration > 0 && (
                  <span className="text-[11px] text-gray-400 mt-1 inline-block">Duration: {Math.floor(item.duration / 60)}m {item.duration % 60}s</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Tabs */}
      <div className="border-b border-gray-200 px-5 pt-4">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-[#1875FF] text-[#1875FF] bg-[#1875FF]/5'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  activeTab === tab.key ? 'bg-[#1875FF]/10 text-[#1875FF]' : 'bg-gray-100 text-gray-500'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default DealActivityTimeline;
